import { lazy as reactLazy, type ComponentType } from 'react'

const CHUNK_RELOAD_KEY = 'oms:lazy-chunk-reload'

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  const name = error instanceof Error ? error.name : ''
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
      msg,
    )
  )
}

/** Retry a failed dynamic import; one guarded full reload for stale deploy hashes. */
function importWithRetry<T>(loader: () => Promise<T>, retries = 1): Promise<T> {
  return loader().catch(async (error) => {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 200))
      return importWithRetry(loader, retries - 1)
    }
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0')
      if (Date.now() - last > 15_000) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
        window.location.reload()
        return new Promise<T>(() => {})
      }
    }
    throw error
  })
}

function lazy<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  return reactLazy(() => importWithRetry(loader))
}

/** Named-export page → default for React.lazy */
function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return reactLazy(async () => {
    const mod = await importWithRetry(loader)
    return { default: mod[exportName] as ComponentType<any> }
  })
}

// Dashboard
export const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'))
export const ShopManagement = lazy(() => import('@/pages/dashboard/ShopManagement'))
export const BranchManagement = lazy(() => import('@/pages/dashboard/BranchManagement'))
export const CustomerManagement = lazy(() => import('@/pages/dashboard/CustomerManagement'))
export const ProductManagement = lazy(() => import('@/pages/dashboard/ProductManagement'))
export const OrderManagement = lazy(() => import('@/pages/dashboard/OrderManagement'))
export const QuotationManagement = lazy(() => import('@/pages/dashboard/QuotationManagement'))
export const OutboundShipmentManagement = lazy(
  () => import('@/pages/dashboard/OutboundShipmentManagement'),
)
export const OutboundShipmentDetail = lazy(() => import('@/pages/dashboard/OutboundShipmentDetail'))
export const PaymentManagement = lazy(() => import('@/pages/dashboard/PaymentManagement'))
export const PaymentIntentsManagement = lazy(
  () => import('@/pages/dashboard/PaymentIntentsManagement'),
)
export const StaffManagement = lazy(() => import('@/pages/dashboard/StaffManagement'))
export const SupplierManagement = lazy(() => import('@/pages/dashboard/SupplierManagement'))
export const BrandManagement = lazy(() => import('@/pages/dashboard/BrandManagement'))
export const PurchaseOrderManagement = lazy(
  () => import('@/pages/dashboard/PurchaseOrderManagement'),
)
export const SubscriptionManagement = lazy(() => import('@/pages/dashboard/SubscriptionManagement'))
export const RentalLeaseManagement = lazy(() => import('@/pages/dashboard/RentalLeaseManagement'))
export const TagManagement = lazy(() => import('@/pages/dashboard/TagManagement'))
export const ReferrerManagement = lazy(() => import('@/pages/dashboard/ReferrerManagement'))
export const DataManagement = lazy(() => import('@/pages/dashboard/DataManagement'))
export const PayrollManagement = lazy(() => import('@/pages/dashboard/PayrollManagement'))
export const StaffTimeEntryManagement = lazy(
  () => import('@/pages/dashboard/StaffTimeEntryManagement'),
)
export const StaffLeaveManagement = lazy(() => import('@/pages/dashboard/StaffLeaveManagement'))
export const ClassPackageManagement = lazy(() => import('@/pages/dashboard/ClassPackageManagement'))
export const JournalManagement = lazy(() => import('@/pages/dashboard/JournalManagement'))
export const VendorBillManagement = lazy(() => import('@/pages/dashboard/VendorBillManagement'))
export const OrderReturnManagement = lazy(() => import('@/pages/dashboard/OrderReturnManagement'))
export const BookingManagement = lazy(() => import('@/pages/dashboard/BookingManagement'))
export const ScheduleManagement = lazy(() => import('@/pages/dashboard/ScheduleManagement'))
export const BillUnlinkedSessionsPage = lazy(
  () => import('@/pages/dashboard/BillUnlinkedSessionsPage'),
)
export const ScheduleResourcesPage = lazy(() => import('@/pages/dashboard/ScheduleResourcesPage'))
export const Reports = lazy(() => import('@/pages/dashboard/Reports'))
export const CustomerProductReconcileReport = lazy(
  () => import('@/pages/dashboard/CustomerProductReconcileReport'),
)
export const SubscriptionReport = lazy(() => import('@/pages/dashboard/SubscriptionReport'))
export const StaffSessionReport = lazy(() => import('@/pages/dashboard/StaffSessionReport'))
export const PaymentReport = lazy(() => import('@/pages/dashboard/PaymentReport'))
export const BookingReport = lazy(() => import('@/pages/dashboard/BookingReport'))
export const AgencyReferrerReport = lazy(() => import('@/pages/dashboard/AgencyReferrerReport'))
export const OrderReport = lazy(() => import('@/pages/dashboard/OrderReport'))
export const InventoryReport = lazy(() => import('@/pages/dashboard/InventoryReport'))
export const BusinessReportPage = lazy(() => import('@/pages/dashboard/BusinessReportPage'))
export const Settings = lazy(() => import('@/pages/dashboard/Settings'))
export const StaffProfile = lazy(() => import('@/pages/dashboard/StaffProfile'))
export const MessageTemplateManagement = lazy(
  () => import('@/pages/dashboard/MessageTemplateManagement'),
)
export const MessageComposer = lazy(() => import('@/pages/dashboard/MessageComposer'))
export const SupportInbox = lazy(() => import('@/pages/dashboard/SupportInbox'))
export const AdminPlatformSupport = lazy(() => import('@/pages/dashboard/AdminPlatformSupport'))
export const TeamManagement = lazy(() => import('@/pages/dashboard/TeamManagement'))
export const CollaborationBoard = lazy(() => import('@/pages/dashboard/CollaborationBoard'))
export const TeamFiles = lazy(() => import('@/pages/dashboard/TeamFiles'))
export const EventManagement = lazy(() => import('@/pages/dashboard/EventManagement'))
export const EventFormManagement = lazy(() => import('@/pages/dashboard/EventFormManagement'))
export const CouponManagement = lazy(() => import('@/pages/dashboard/CouponManagement'))
export const SocialPromote = lazy(() => import('@/pages/dashboard/SocialPromote'))
export const CompanionBundleManagement = lazy(
  () => import('@/pages/dashboard/CompanionBundleManagement'),
)
export const MembershipManagement = lazy(() => import('@/pages/dashboard/MembershipManagement'))
export const AutomationTasks = lazy(() => import('@/pages/dashboard/AutomationTasks'))
export const WebsiteDesigner = lazy(() => import('@/pages/dashboard/WebsiteDesigner'))
export const PageEditor = lazy(() => import('@/pages/dashboard/PageEditor'))
export const WebsiteBlogPage = lazy(() => import('@/pages/dashboard/WebsiteBlogPage'))
export const WebsiteTemplatesPage = lazy(() => import('@/pages/dashboard/WebsiteTemplatesPage'))
export const WebsiteAnalyticsPage = lazy(() => import('@/pages/dashboard/WebsiteAnalyticsPage'))
export const WebsiteReviewsPage = lazy(() => import('@/pages/dashboard/WebsiteReviewsPage'))
export const AdminPlatform = lazy(() => import('@/pages/dashboard/AdminPlatform'))
export const AdminPlatformRedirect = lazyNamed(
  () => import('@/pages/dashboard/AdminPlatform'),
  'AdminPlatformRedirect',
)
export const AdminPlatformSubscriptions = lazy(
  () => import('@/pages/dashboard/AdminPlatformSubscriptions'),
)
export const AdminPlatformNotifications = lazy(
  () => import('@/pages/dashboard/AdminPlatformNotifications'),
)
export const AdminPlatformNurture = lazy(() => import('@/pages/dashboard/AdminPlatformNurture'))
export const HostBackups = lazy(() => import('@/pages/dashboard/HostBackups'))
export const PlatformReports = lazy(() => import('@/pages/dashboard/PlatformReports'))
export const PlatformRemittanceReport = lazy(
  () => import('@/pages/dashboard/PlatformRemittanceReport'),
)
export const Unauthorized = lazy(() => import('@/pages/dashboard/Unauthorized'))

// Customer portal
export const CustomerDashboard = lazy(() => import('@/pages/customer/CustomerDashboard'))
export const ProductCatalog = lazy(() => import('@/pages/customer/ProductCatalog'))
export const ShoppingCart = lazy(() => import('@/pages/customer/ShoppingCart'))
export const OrderHistory = lazy(() => import('@/pages/customer/OrderHistory'))
export const CustomerOrderDetail = lazy(() => import('@/pages/customer/CustomerOrderDetail'))
export const CustomerDownloads = lazy(() => import('@/pages/customer/CustomerDownloads'))
export const CustomerTickets = lazy(() => import('@/pages/customer/CustomerTickets'))
export const CustomerRegisterPayment = lazy(() => import('@/pages/customer/CustomerRegisterPayment'))
export const CustomerPaymentReturn = lazy(() => import('@/pages/customer/CustomerPaymentReturn'))
export const MyCoupons = lazy(() => import('@/pages/customer/MyCoupons'))
export const MyMembership = lazy(() => import('@/pages/customer/MyMembership'))
export const MySubscriptions = lazy(() => import('@/pages/customer/MySubscriptions'))
export const CustomerSubscriptionDetail = lazy(
  () => import('@/pages/customer/CustomerSubscriptionDetail'),
)
export const CustomerProfile = lazy(() => import('@/pages/customer/CustomerProfile'))
export const CustomerSchedule = lazy(() => import('@/pages/customer/CustomerSchedule'))
export const CustomerSupport = lazy(() => import('@/pages/customer/CustomerSupport'))

// Public / help / signup (heavier surfaces)
export const HomePage = lazy(() => import('@/pages/public/HomePage'))
export const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
export const PartnersPage = lazy(() => import('@/pages/public/PartnersPage'))
export const OnboardingPage = lazy(() => import('@/pages/public/OnboardingPage'))
export const ExploreDashboardPage = lazy(() => import('@/pages/public/ExploreDashboardPage'))
export const HelpHubPage = lazy(() => import('@/pages/help/HelpHubPage'))
export const HelpContactPage = lazy(() => import('@/pages/help/HelpContactPage'))
export const HelpCategoryPage = lazy(() => import('@/pages/help/HelpCategoryPage'))
export const HelpArticlePage = lazy(() => import('@/pages/help/HelpArticlePage'))
export const TermsPage = lazyNamed(() => import('@/pages/public/LegalPages'), 'TermsPage')
export const PrivacyPage = lazyNamed(() => import('@/pages/public/LegalPages'), 'PrivacyPage')
export const DpaPage = lazyNamed(() => import('@/pages/public/LegalPages'), 'DpaPage')
export const ShopCatalog = lazy(() => import('@/pages/public/ShopCatalog'))
export const PublicForm = lazy(() => import('@/pages/public/PublicForm'))
export const PublicOrderPayPage = lazy(() => import('@/pages/public/PublicOrderPayPage'))
export const PublicOrderPayReturnPage = lazy(
  () => import('@/pages/public/PublicOrderPayReturnPage'),
)
export const SignupRegisterPage = lazy(() => import('@/pages/signup/SignupRegisterPage'))
export const SignupCheckEmailPage = lazy(() => import('@/pages/signup/SignupCheckEmailPage'))
export const SignupVerifyPage = lazy(() => import('@/pages/signup/SignupVerifyPage'))
export const SignupContinuePage = lazy(() => import('@/pages/signup/SignupContinuePage'))
export const SignupSetupPage = lazy(() => import('@/pages/signup/SignupSetupPage'))
